import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RivetsApiError,
  RivetsClient,
  type ApiTokensResponse,
  type RedeemAccountLinkRequest,
  type CreateApiTokenRequestInput,
  type DecideTaskApprovalRequest,
  type StartChannelInstallRequest,
  type TaskApproval,
  type TaskApprovalResponse,
} from "@rivets/core";
import { qk } from "@/lib/query-keys";

/**
 * Single shared REST client. Relative base URL + cookie credentials — the
 * Vite dev proxy forwards `/api` to the local worker; in production the SPA
 * is served from the same origin as the API.
 */
export const client = new RivetsClient({ baseUrl: "" });

/**
 * A 4xx will not become a 2xx on a second attempt, and neither will the 501 a
 * route serves while its backend is still being built — retrying either only
 * delays the error state the user is meant to see. Everything else (a 500, a
 * dropped connection) gets the app's usual single retry.
 */
export function retryTransient(count: number, error: unknown): boolean {
  const permanent = error instanceof RivetsApiError && (error.status < 500 || error.status === 501);
  return !permanent && count < 1;
}

// -- api tokens ---------------------------------------------------------------

/** Personal MCP/API tokens for the signed-in member of this org. */
export function useApiTokens(orgId: string) {
  return useQuery({
    queryKey: qk.apiTokens(orgId),
    queryFn: () => client.listApiTokens(orgId),
    retry: retryTransient,
  });
}

/** The plaintext secret exists only in this mutation's result. */
export function useCreateApiToken(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateApiTokenRequestInput) => client.createApiToken(orgId, body),
    onSuccess: (created) => {
      queryClient.setQueryData<ApiTokensResponse>(qk.apiTokens(orgId), (old) =>
        old ? { tokens: [created.token, ...old.tokens] } : old,
      );
    },
  });
}

export function useRevokeApiToken(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => client.revokeApiToken(orgId, tokenId),
    onSuccess: (_result, tokenId) => {
      queryClient.setQueryData<ApiTokensResponse>(qk.apiTokens(orgId), (old) =>
        old
          ? { tokens: old.tokens.map((t) => (t.id === tokenId ? { ...t, revokedAt: Date.now() } : t)) }
          : old,
      );
    },
  });
}

// -- channels (Slack, Teams) --------------------------------------------------

export function useChannelInstallations(orgId: string) {
  return useQuery({
    queryKey: qk.channels(orgId),
    queryFn: () => client.listChannelInstallations(orgId),
    retry: retryTransient,
  });
}

export function useChannelIdentityLink(orgId: string, provider: string, enabled = true) {
  return useQuery({
    queryKey: qk.channelIdentity(orgId, provider),
    queryFn: () => client.channelIdentityLink(orgId, provider),
    retry: retryTransient,
    enabled,
  });
}

export function useStartChannelInstall(orgId: string, provider: string) {
  return useMutation({
    mutationFn: (body?: StartChannelInstallRequest) => client.startChannelInstall(orgId, provider, body),
  });
}

export function useUninstallChannel(orgId: string, provider: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.uninstallChannel(orgId, provider),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.channels(orgId) }),
  });
}

/** What an account-link challenge would bind. Reading it consumes nothing. */
export function useAccountLinkPreview(token: string) {
  return useQuery({
    queryKey: qk.accountLink(token),
    queryFn: () => client.accountLinkPreview(token),
    retry: retryTransient,
    enabled: /^[0-9a-f]{64}$/i.test(token),
    staleTime: Infinity,
  });
}

/** Redeems a challenge the member has explicitly confirmed. */
export function useRedeemAccountLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RedeemAccountLinkRequest) => client.redeemAccountLink(body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["channel-identity"] }),
  });
}

// -- task approvals -----------------------------------------------------------
//
// `RivetsClient` has no approval methods yet, so these speak to the routes
// directly with the same relative base, cookie session and error shape
// (`RivetsApiError` with the route's `code`) as every other call.

export type TaskApprovalDecision = "approve" | "reject";

async function approvalRequest<T>(
  method: "GET" | "POST",
  path: string,
  { body, signal, fetcher = fetch }: { body?: unknown; signal?: AbortSignal; fetcher?: typeof fetch } = {},
): Promise<T> {
  const response = await fetcher(path, {
    method,
    credentials: "same-origin",
    headers: { accept: "application/json", ...(body !== undefined ? { "content-type": "application/json" } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    let message = response.statusText;
    let code: string | undefined;
    try {
      const data = (await response.json()) as { error?: string; message?: string; code?: string };
      message = data.error ?? data.message ?? message;
      code = data.code;
    } catch {
      /* non-JSON error body */
    }
    throw new RivetsApiError(response.status, message, code);
  }
  return (await response.json()) as T;
}

/** The chat's approval, pending or decided, or null when it never needed one. */
export async function fetchTaskApproval(chatId: string, signal?: AbortSignal, fetcher?: typeof fetch): Promise<TaskApproval | null> {
  try {
    const { approval } = await approvalRequest<TaskApprovalResponse>("GET", `/api/chats/${encodeURIComponent(chatId)}/approval`, { signal, fetcher });
    return approval;
  } catch (error) {
    if (error instanceof RivetsApiError && error.status === 404) return null;
    throw error;
  }
}

export async function decideTaskApproval(
  chatId: string,
  decision: TaskApprovalDecision,
  clientRequestId: string,
  fetcher?: typeof fetch,
): Promise<TaskApproval> {
  const body: DecideTaskApprovalRequest = { clientRequestId };
  const { approval } = await approvalRequest<TaskApprovalResponse>(
    "POST",
    `/api/chats/${encodeURIComponent(chatId)}/approval/${decision}`,
    { body, fetcher },
  );
  return approval;
}
